import React, { FC, useState } from 'react';
import TextInput from 'components/Elements/TextInput';
import Checkbox from 'components/Elements/Checkbox';
import type { SaveKeyReferenceValues } from 'pages/VisitorsOnSite/types';
import { MAX_LONG_TEXT_LENGTH, MAX_SHORT_TEXT_LENGTH } from 'core/constants';
import './keyReferenceModal.scss';

interface KeyReferenceModalProps {
    onSave: (values: SaveKeyReferenceValues) => void;
    onCancel: () => void;
    selectedVisitorIds: string[];
}

const KeyReferenceModal: FC<KeyReferenceModalProps> = ({ onSave, onCancel, selectedVisitorIds }) => {
    const [keyReference, setKeyReference] = useState<string>('');
    const [description, setDescription] = useState<string>('');
    const [applyToAll, setApplyToAll] = useState<boolean>(false);
    const [error, setError] = useState<string>('');
    const [descriptionError, setDescriptionError] = useState<string>('');
    const [keyReferenceError, setKeyReferenceError] = useState<string>('');

    const handleKeyReferenceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { value } = e.target;
        if (value.length > MAX_SHORT_TEXT_LENGTH) {
            setKeyReferenceError(`You have reached the maximum character limit of ${MAX_SHORT_TEXT_LENGTH}!`);
        } else {
            setKeyReferenceError('');
            setKeyReference(value);
        }
    };

    const handleDescriptionChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        const { value } = e.target;
        if (value.length > MAX_LONG_TEXT_LENGTH) {
            setDescriptionError(`You have reached the maximum character limit of ${MAX_LONG_TEXT_LENGTH}!`);
        } else {
            setDescriptionError('');
            setDescription(value);
        }
    };

    const handleApplyToAllClick = () => {
        setApplyToAll(!applyToAll);
    };

    const handleSaveClick = () => {
        if (keyReference.trim() === '') {
            setError('Please enter a key reference');
            return;
        }
        onSave({ keyReference: keyReference.trim(), description: description.trim(), applyToAll });
        setError('');
        setKeyReference('');
        setDescription('');
        setApplyToAll(false);
    };

    const handleCancelClick = () => {
        // Clear any errors when canceling
        setError('');
        setKeyReferenceError('');
        setDescriptionError('');
        onCancel();
    };

    return (
        <div className="key-reference-modal-container">
            <h1 className="heading">Issue Key</h1>
            <div className="content-wrapper">
                <div className="reference-wrapper">
                    <p className="caption mb-8">Key Reference</p>
                    <TextInput
                        value={keyReference}
                        onChange={handleKeyReferenceChange}
                        maxLength={MAX_SHORT_TEXT_LENGTH}
                        placeholder="Enter key reference"
                        aria-label="Key reference input" />
                    {keyReferenceError && <p className="error">{keyReferenceError}</p>}
                </div>
                <div className="description-wrapper">
                    <p className="caption mb-8">Description</p>
                    <textarea
                        value={description}
                        onChange={handleDescriptionChange}
                        maxLength={MAX_LONG_TEXT_LENGTH}
                        placeholder="Enter description (optional)"
                        aria-label="Description textarea" />
                    {descriptionError && <p className="error">{descriptionError}</p>}
                </div>

                {selectedVisitorIds.length > 0 ? (
                    <div className="apply-to-all-wrapper">
                        <Checkbox checked={applyToAll} onClick={handleApplyToAllClick} />
                        <span className="label">Apply this Key Reference to all selected visitors</span>
                    </div>
                ) : null}

                <div className="button-wrapper">
                    <button
                        onClick={handleSaveClick}
                        className="save-button"
                        type="button"
                        aria-label="Save key reference">Save
                    </button>
                    {error && <p className="error">{error}</p>}
                    <button
                        onClick={handleCancelClick}
                        className="cancel-button"
                        type="button"
                        aria-label="Cancel operation">Cancel
                    </button>
                </div>
            </div>
        </div>
    );
};

export default KeyReferenceModal;
